import { Platform } from 'react-native';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  writeBatch,
  Timestamp,
  increment,
  arrayUnion,
  limit,
  Unsubscribe,
  DocumentData,
  QuerySnapshot,
} from 'firebase/firestore';
import { getFirestoreInstance } from './config';
import {
  FirebaseChat,
  FirebaseMessage,
  CreateChatInput,
  SendMessageInput,
  ChatParticipant,
  ChatListItem,
  MessageItem,
} from './types';

// Kolekcje Firestore
const CHATS_COLLECTION = 'chats';
const MESSAGES_COLLECTION = 'messages';
const FCM_TOKENS_COLLECTION = 'fcmTokens';
const APPLICATION_NOTIFICATIONS_COLLECTION = 'applicationNotifications';

/**
 * Serwis do obsługi czatu Firebase (Web SDK)
 */
class ChatService {
  private get db() {
    return getFirestoreInstance();
  }

  private get isAvailable(): boolean {
    return this.db !== null;
  }

  /**
   * Pobierz lub utwórz czat między dwoma użytkownikami dla konkretnego ogłoszenia
   * Czaty są unikalne per ogłoszenie + uczestnik
   */
  async getOrCreateChat(
    currentUserId: string,
    currentUserData: ChatParticipant,
    input: CreateChatInput
  ): Promise<string> {
    if (!this.isAvailable || !this.db) {
      console.warn('ChatService: Firebase not available');
      return '';
    }

    const announcementId = input.meetingInfo?.announcementId;

    // Sprawdź czy czat już istnieje dla tego ogłoszenia
    const existingChat = await this.findChatForAnnouncement(
      currentUserId,
      input.participantId,
      announcementId
    );

    if (existingChat) {
      return existingChat.id;
    }

    // Utwórz nowy czat
    const participants = [currentUserId, input.participantId].sort();
    const now = Timestamp.now();

    const chatData: Omit<FirebaseChat, 'id'> = {
      participants,
      participantsData: {
        [currentUserId]: currentUserData,
        [input.participantId]: input.participantData,
      },
      announcementId,
      createdAt: now,
      updatedAt: now,
      unreadCount: {
        [currentUserId]: 0,
        [input.participantId]: 0,
      },
      meetingInfo: input.meetingInfo,
    };

    const docRef = await addDoc(collection(this.db, CHATS_COLLECTION), chatData);
    return docRef.id;
  }

  /**
   * Znajdź czat między dwoma użytkownikami dla konkretnego ogłoszenia
   */
  async findChatForAnnouncement(
    userId1: string,
    userId2: string,
    announcementId?: string
  ): Promise<FirebaseChat | null> {
    if (!this.isAvailable || !this.db) return null;

    const participants = [userId1, userId2].sort();

    // Szukaj czatu z tymi samymi uczestnikami i announcementId
    let q;
    if (announcementId) {
      q = query(
        collection(this.db, CHATS_COLLECTION),
        where('participants', '==', participants),
        where('announcementId', '==', announcementId),
        limit(1)
      );
    } else {
      // Dla wstecznej kompatybilności - jeśli nie ma announcementId, szukaj czatu bez niego
      q = query(
        collection(this.db, CHATS_COLLECTION),
        where('participants', '==', participants),
        limit(1)
      );
    }

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const docSnap = snapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() } as FirebaseChat;
  }

  /**
   * Subskrybuj czaty użytkownika (real-time)
   */
  subscribeToChats(
    userId: string,
    onChats: (chats: ChatListItem[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!this.isAvailable || !this.db) {
      // Firebase nie jest dostępny - zwróć pustą listę bez błędu
      console.log('ChatService: Firebase not available, returning empty chat list');
      onChats([]);
      return () => {};
    }

    try {
      const q = query(
        collection(this.db, CHATS_COLLECTION),
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc')
      );

      return onSnapshot(
        q,
        (snapshot: QuerySnapshot<DocumentData>) => {
          const chats = snapshot.docs.map((docSnap) => {
            const data = docSnap.data() as Omit<FirebaseChat, 'id'>;
            return this.mapChatToListItem(docSnap.id, data, userId);
          });
          onChats(chats);
        },
        (error: Error) => {
          console.error('Error subscribing to chats:', error);
          // Jeśli błąd dotyczy braku indeksu lub uprawnień, zwróć pustą listę
          if (error.message?.includes('index') || error.message?.includes('permission')) {
            console.warn('ChatService: Index or permission error, returning empty list');
            onChats([]);
          } else {
            onError?.(error);
          }
        }
      );
    } catch (error) {
      console.error('ChatService: Error creating query:', error);
      onChats([]);
      return () => {};
    }
  }

  /**
   * Subskrybuj wiadomości w czacie (real-time) z limitem
   */
  subscribeToMessages(
    chatId: string,
    currentUserId: string,
    onMessages: (messages: MessageItem[]) => void,
    onError?: (error: Error) => void,
    messageLimit: number = 30
  ): Unsubscribe {
    if (!this.isAvailable || !this.db) {
      onMessages([]);
      return () => {};
    }

    // Get last N messages ordered by timestamp descending, then reverse for display
    const q = query(
      collection(this.db, CHATS_COLLECTION, chatId, MESSAGES_COLLECTION),
      orderBy('timestamp', 'desc'),
      limit(messageLimit)
    );

    return onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        // Reverse to get chronological order (oldest first)
        const reversedDocs = [...snapshot.docs].reverse();
        const messages = this.mapMessagesToItems(reversedDocs, currentUserId);
        onMessages(messages);
      },
      (error: Error) => {
        console.error('Error subscribing to messages:', error);
        onError?.(error);
      }
    );
  }

  /**
   * Pobierz starsze wiadomości (dla paginacji)
   */
  async getOlderMessages(
    chatId: string,
    currentUserId: string,
    beforeTimestamp: Date,
    messageLimit: number = 20
  ): Promise<MessageItem[]> {
    if (!this.isAvailable || !this.db) {
      return [];
    }

    const q = query(
      collection(this.db, CHATS_COLLECTION, chatId, MESSAGES_COLLECTION),
      orderBy('timestamp', 'desc'),
      where('timestamp', '<', Timestamp.fromDate(beforeTimestamp)),
      limit(messageLimit)
    );

    const snapshot = await getDocs(q);
    // Reverse to get chronological order
    const reversedDocs = [...snapshot.docs].reverse();
    return this.mapMessagesToItems(reversedDocs, currentUserId);
  }

  /**
   * Wyślij wiadomość
   */
  async sendMessage(
    currentUserId: string,
    input: SendMessageInput
  ): Promise<string> {
    if (!this.isAvailable || !this.db) {
      console.warn('ChatService: Firebase not available');
      return '';
    }

    const { chatId, text, type = 'text', giftData } = input;
    const now = Timestamp.now();

    const messageData: Omit<FirebaseMessage, 'id'> = {
      chatId,
      senderId: currentUserId,
      text,
      timestamp: now,
      readBy: [currentUserId],
      type,
      ...(giftData && { giftData }),
    };

    // Dodaj wiadomość do subkolekcji
    const messageRef = await addDoc(
      collection(this.db, CHATS_COLLECTION, chatId, MESSAGES_COLLECTION),
      messageData
    );

    // Zaktualizuj ostatnią wiadomość w czacie i pobierz dane o uczestnikach
    const chatRef = doc(this.db, CHATS_COLLECTION, chatId);
    const chatDoc = await getDoc(chatRef);
    const chatData = chatDoc.data() as FirebaseChat;
    
    // Znajdź drugiego uczestnika
    const otherUserId = chatData.participants.find((p) => p !== currentUserId);

    await updateDoc(chatRef, {
      lastMessage: {
        text,
        senderId: currentUserId,
        timestamp: now,
      },
      updatedAt: now,
      [`unreadCount.${otherUserId}`]: increment(1),
    });

    return messageRef.id;
  }

  /**
   * Oznacz wiadomości jako przeczytane
   * Tylko resetuje licznik unreadCount - nie aktualizuje pojedynczych wiadomości
   * aby uniknąć dużej liczby writes
   */
  async markMessagesAsRead(chatId: string, userId: string): Promise<void> {
    if (!this.isAvailable || !this.db) return;

    const chatRef = doc(this.db, CHATS_COLLECTION, chatId);
    
    // Zawsze zapisz lastReadAt i zresetuj unreadCount
    // Nie sprawdzamy czy unreadCount > 0 żeby uniknąć race condition
    const now = Timestamp.now();
    await updateDoc(chatRef, {
      [`unreadCount.${userId}`]: 0,
      [`lastReadAt.${userId}`]: now,
    });
  }

  /**
   * Zapisz token FCM dla użytkownika
   */
  async saveFCMToken(
    userId: string,
    token: string,
    platform: 'ios' | 'android' | 'web'
  ): Promise<void> {
    if (!this.isAvailable || !this.db) return;

    const tokenRef = doc(this.db, FCM_TOKENS_COLLECTION, userId);
    await updateDoc(tokenRef, {
      odataId: userId,
      token,
      platform,
      updatedAt: Timestamp.now(),
    }).catch(async () => {
      // Dokument nie istnieje, utwórz nowy
      await addDoc(collection(this.db!, FCM_TOKENS_COLLECTION), {
        odataId: userId,
        token,
        platform,
        updatedAt: Timestamp.now(),
      });
    });
  }

  /**
   * Pobierz token FCM użytkownika
   */
  async getFCMToken(userId: string): Promise<string | null> {
    if (!this.isAvailable || !this.db) return null;

    const tokenRef = doc(this.db, FCM_TOKENS_COLLECTION, userId);
    const docSnap = await getDoc(tokenRef);
    if (!docSnap.exists()) return null;
    return docSnap.data()?.token || null;
  }

  /**
   * Usuń token FCM użytkownika (przy wylogowaniu)
   */
  async removeFCMToken(userId: string): Promise<void> {
    if (!this.isAvailable || !this.db) return;

    const batch = writeBatch(this.db);
    const tokenRef = doc(this.db, FCM_TOKENS_COLLECTION, userId);
    batch.delete(tokenRef);
    await batch.commit();
  }

  /**
   * Pobierz szczegóły czatu
   */
  async getChat(chatId: string): Promise<FirebaseChat | null> {
    if (!this.isAvailable || !this.db) return null;

    const chatRef = doc(this.db, CHATS_COLLECTION, chatId);
    const docSnap = await getDoc(chatRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as FirebaseChat;
  }

  /**
   * Aktualizuj dane uczestnika w czatach (np. po zmianie avatara)
   */
  async updateParticipantData(
    userId: string,
    updatedData: Partial<ChatParticipant>
  ): Promise<void> {
    if (!this.isAvailable || !this.db) return;

    const q = query(
      collection(this.db, CHATS_COLLECTION),
      where('participants', 'array-contains', userId)
    );

    const chats = await getDocs(q);
    const batch = writeBatch(this.db);

    chats.docs.forEach((docSnap) => {
      const currentData = docSnap.data().participantsData[userId] || {};
      batch.update(docSnap.ref, {
        [`participantsData.${userId}`]: {
          ...currentData,
          ...updatedData,
        },
      });
    });

    await batch.commit();
  }

  // ============ Mapowanie danych ============

  /**
   * Mapuj FirebaseChat na ChatListItem dla UI
   */
  private mapChatToListItem(
    id: string,
    data: Omit<FirebaseChat, 'id'>,
    currentUserId: string
  ): ChatListItem {
    // Znajdź drugiego uczestnika
    const otherUserId = data.participants.find((p) => p !== currentUserId) || '';
    const otherParticipant = data.participantsData[otherUserId] || {
      odataId: otherUserId,
      odataName: 'Nieznany',
    };

    const lastMessageTime = data.lastMessage?.timestamp?.toDate() || data.createdAt.toDate();

    return {
      id,
      odataName: otherParticipant.odataName,
      odataAvatar: otherParticipant.odataAvatar || '',
      lastMessage: data.lastMessage?.text || 'Rozpocznij rozmowę...',
      lastMessageTime,
      unreadCount: data.unreadCount?.[currentUserId] || 0,
      verified: otherParticipant.odataVerified || false,
      meetingInfo: data.meetingInfo,
      otherParticipant,
    };
  }

  /**
   * Mapuj dokumenty wiadomości na MessageItem[] z separatorami dat
   */
  private mapMessagesToItems(
    docs: any[],
    currentUserId: string
  ): MessageItem[] {
    const items: MessageItem[] = [];
    let lastDate = '';

    docs.forEach((docSnap) => {
      const data = docSnap.data() as Omit<FirebaseMessage, 'id'>;
      const timestamp = data.timestamp.toDate();
      const dateStr = this.formatDateSeparator(timestamp);

      // Dodaj separator daty jeśli zmienił się dzień
      if (dateStr !== lastDate) {
        items.push({
          id: `date-${docSnap.id}`,
          text: '',
          time: '',
          isOwn: false,
          timestamp,
          type: 'text',
          date: dateStr,
        });
        lastDate = dateStr;
      }

      // Dodaj wiadomość
      items.push({
        id: docSnap.id,
        text: data.text,
        time: this.formatTime(timestamp),
        isOwn: data.senderId === currentUserId,
        timestamp,
        type: data.type,
        giftData: data.giftData,
      });
    });

    return items;
  }

  /**
   * Formatuj datę dla separatora
   */
  private formatDateSeparator(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (this.isSameDay(date, today)) {
      return 'Dzisiaj';
    } else if (this.isSameDay(date, yesterday)) {
      return 'Wczoraj';
    } else {
      return date.toLocaleDateString('pl-PL', {
        day: 'numeric',
        month: 'long',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
      });
    }
  }

  /**
   * Formatuj czas wiadomości
   */
  private formatTime(date: Date): string {
    return date.toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Sprawdź czy dwie daty są tego samego dnia
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  // ============ Powiadomienia o aplikacjach ============

  /**
   * Wyślij powiadomienie o nowej aplikacji do właściciela ogłoszenia
   * Wywoływane przez frontend aplikującego po złożeniu aplikacji
   */
  async sendApplicationNotification(
    hostUserId: string,
    applicationId: string,
    announcementId: string,
    applicantId: string
  ): Promise<void> {
    if (!this.isAvailable || !this.db) {
      console.warn('ChatService: Firebase not available for application notification');
      return;
    }

    try {
      const notificationRef = doc(
        this.db,
        APPLICATION_NOTIFICATIONS_COLLECTION,
        hostUserId,
        'pending',
        applicationId
      );

      await updateDoc(notificationRef, {
        announcementId,
        applicantId,
        createdAt: Timestamp.now(),
      }).catch(async () => {
        // Document doesn't exist, create it using setDoc
        const { setDoc } = await import('firebase/firestore');
        await setDoc(notificationRef, {
          announcementId,
          applicantId,
          createdAt: Timestamp.now(),
        });
      });

      console.log('Application notification sent to host:', hostUserId);
    } catch (error) {
      console.error('Error sending application notification:', error);
    }
  }

  /**
   * Nasłuchuj powiadomień o nowych aplikacjach dla użytkownika (właściciela ogłoszeń)
   * Zwraca funkcję unsubscribe
   */
  subscribeToApplicationNotifications(
    userId: string,
    onNotification: () => void
  ): Unsubscribe {
    if (!this.isAvailable || !this.db) {
      console.warn('ChatService: Firebase not available for notifications subscription');
      return () => {};
    }

    const notificationsRef = collection(
      this.db,
      APPLICATION_NOTIFICATIONS_COLLECTION,
      userId,
      'pending'
    );

    let isFirstSnapshot = true;

    return onSnapshot(
      notificationsRef,
      (snapshot) => {
        // Pomiń pierwszy snapshot (początkowe dane)
        if (isFirstSnapshot) {
          isFirstSnapshot = false;
          return;
        }

        // Reaguj tylko na dodane dokumenty
        const hasNewDocs = snapshot.docChanges().some(change => change.type === 'added');
        if (hasNewDocs) {
          console.log('New application notification received');
          onNotification();
        }
      },
      (error) => {
        console.error('Error listening to application notifications:', error);
      }
    );
  }

  /**
   * Usuń powiadomienia o aplikacjach dla danego ogłoszenia (po oznaczeniu jako przeczytane)
   */
  async clearApplicationNotifications(
    userId: string,
    announcementId?: string
  ): Promise<void> {
    if (!this.isAvailable || !this.db) return;

    try {
      const notificationsRef = collection(
        this.db,
        APPLICATION_NOTIFICATIONS_COLLECTION,
        userId,
        'pending'
      );

      let q;
      if (announcementId) {
        q = query(notificationsRef, where('announcementId', '==', announcementId));
      } else {
        q = notificationsRef;
      }

      const snapshot = await getDocs(q);
      const batch = writeBatch(this.db);

      snapshot.docs.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });

      await batch.commit();
      console.log('Application notifications cleared');
    } catch (error) {
      console.error('Error clearing application notifications:', error);
    }
  }
}

// Singleton instance
export const chatService = new ChatService();
export default chatService;
